import { Badge } from '@/components/ui';
import { ActionMenu } from '@/components/ui/ActionMenu';
import { TablePagination } from '@/components/ui/TablePagination';
import { Eye, Edit, Trash2 } from 'lucide-react';
import type { EquipmentListItem } from '../types/equipmentList.types';

const STATUS_MAP: Record<string, { label: string; className: string }> = {
  active:       { label: 'Hoạt động',    className: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  inactive:     { label: 'Không hoạt động', className: 'bg-slate-100 text-slate-500 border-slate-200' },
  available:    { label: 'Sẵn sàng',     className: 'bg-blue-50 text-blue-600 border-blue-100' },
  in_use:       { label: 'Đang sử dụng', className: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  assigned:     { label: 'Đã điều phối', className: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  maintenance:  { label: 'Bảo trì',      className: 'bg-orange-50 text-orange-600 border-orange-100' },
  repairing:    { label: 'Đang sửa',     className: 'bg-amber-50 text-amber-600 border-amber-100' },
  damaged:      { label: 'Hư hỏng',      className: 'bg-rose-50 text-rose-600 border-rose-100' },
  broken:       { label: 'Hỏng',         className: 'bg-rose-50 text-rose-600 border-rose-100' },
  retired:      { label: 'Thanh lý',     className: 'bg-slate-100 text-slate-500 border-slate-200' },
  lost:         { label: 'Mất/Thất lạc', className: 'bg-slate-100 text-slate-400 border-slate-200' },
};

interface EquipmentTableProps {
  items: EquipmentListItem[];
  total: number;
  page: number;
  pageSize: number;
  isLoading?: boolean;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (item: EquipmentListItem) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      <td className="px-4 py-3"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-md bg-slate-100 shrink-0" /><div className="space-y-1.5"><div className="h-3 bg-slate-100 rounded w-32" /><div className="h-2.5 bg-slate-100 rounded w-20" /></div></div></td>
      <td className="px-4 py-3"><div className="h-3 bg-slate-100 rounded w-24" /></td>
      <td className="px-4 py-3"><div className="h-3 bg-slate-100 rounded w-20" /></td>
      <td className="px-4 py-3"><div className="h-5 bg-slate-100 rounded w-20" /></td>
      <td className="px-4 py-3"><div className="h-3 bg-slate-100 rounded w-24" /></td>
      <td className="px-4 py-3"><div className="h-3 bg-slate-100 rounded w-20" /></td>
      <td className="px-4 py-3"><div className="h-4 bg-slate-100 rounded w-8" /></td>
    </tr>
  );
}

export function EquipmentTable({
  items, total, page, pageSize, isLoading,
  onView, onEdit, onDelete, onPageChange, onPageSizeChange,
  canEdit, canDelete,
}: EquipmentTableProps) {
  return (
    <div className="min-h-[520px] flex-1 flex-col flex bg-white">
      <div className="flex-1 overflow-x-auto">
        <table className="w-full border-collapse text-left text-xs">
          <thead>
            <tr className="border-b border-slate-200 text-slate-500">
              <th className="p-3 text-center font-normal">Mã thiết bị</th>
              <th className="p-3 text-left font-normal">Tên thiết bị</th>
              <th className="p-3 text-center font-normal">Nhóm</th>
              <th className="p-3 text-center font-normal">Trạng thái</th>
              <th className="p-3 text-center font-normal">Người phụ trách</th>
              <th className="p-3 text-center font-normal">Ngày bảo trì tiếp</th>
              <th className="p-3 text-center font-normal">Thao tác</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading
              ? Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
              : items.length === 0
              ? (
                <tr>
                  <td colSpan={7} className="p-10 text-center text-xs text-slate-500">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center">
                        <i className="ri-tools-line text-xl" />
                      </div>
                      <p>Không có thiết bị nào</p>
                    </div>
                  </td>
                </tr>
              )
              : items.map((item) => {
                const normalizedStatus = (item.status_code || '').trim().toLowerCase();
                const status = STATUS_MAP[normalizedStatus] ?? { label: item.status_code, className: 'bg-slate-50 text-slate-500' };
                return (
                  <tr
                    key={item.id}
                    onClick={() => onView(item.id)}
                    className="cursor-pointer hover:bg-slate-50 transition-colors"
                  >
                    <td className="p-3 text-center text-slate-900 font-medium">
                      {item.code || '-'}
                    </td>
                    <td className="p-3 text-left">
                      <div className="flex items-center gap-3">
                        <img 
                          src={item.photo_url || '/assets/other/img/equipment.png'} 
                          alt="" 
                          className="w-10 h-10 rounded-md object-cover border border-slate-200 shrink-0" 
                        />
                        <div>
                          <p className="font-medium text-slate-900 truncate max-w-[200px]">{item.name}</p>
                          {item.brand && <p className="text-xs text-slate-500 mt-0.5 truncate max-w-[200px]">{item.brand} {item.model || ''}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-center text-slate-700">
                      {item.group_name || 'Khác'}
                    </td>
                    <td className="p-3 text-center">
                      <Badge variant="custom" className={`rounded-md border font-medium px-2.5 py-0.5 ${status.className}`}>
                        {status.label}
                      </Badge>
                    </td>
                    <td className="p-3 text-center text-slate-700">
                      {item.assigned_to_name || '-'}
                    </td>
                    <td className="p-3 text-center text-slate-700">
                      {item.next_service_due
                        ? new Date(item.next_service_due).toLocaleDateString('vi-VN')
                        : '-'}
                    </td>
                    <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-center">
                        <ActionMenu
                          actions={[
                            { label: 'Xem chi tiết', icon: <Eye className="w-4 h-4" />, onClick: () => onView(item.id) },
                            ...(canEdit ? [{ label: 'Chỉnh sửa', icon: <Edit className="w-4 h-4" />, onClick: () => onEdit(item.id) }] : []),
                            ...(canDelete ? [{ label: 'Xóa', icon: <Trash2 className="w-4 h-4" />, onClick: () => onDelete(item), destructive: true }] : []),
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      <div className="mt-auto border-t border-slate-200 px-3">
        <TablePagination
          totalItems={total}
          currentPage={page}
          pageSize={pageSize}
          itemLabel="thiết bị"
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageSizeOptions={[10, 20, 50]}
          className="flex-1"
        />
      </div>
    </div>
  );
}
